import { Box, Center, Flex, Icon, SimpleGrid, Text } from '@chakra-ui/react';
import { FaCheck } from '@react-icons/all-files/fa/FaCheck';
import { Fragment, ReactNode } from 'react';

type PillOption = {
  value: string;
  label: string;
  description?: string;
};

type PillSelectProps = {
  options: PillOption[];
  value: string;
  onChange: (value: string) => void;
  afterSelected?: ReactNode;
};

const CheckCircle = ({ checked }: { checked: boolean }) => (
  <Center
    w="22px"
    h="22px"
    borderRadius="full"
    border="2px solid"
    borderColor={checked ? 'oceanBoatBlue' : 'gray.300'}
    bg={checked ? 'oceanBoatBlue' : 'transparent'}
    flexShrink={0}
    transition="all 0.15s ease"
  >
    {checked && <Icon as={FaCheck} color="white" boxSize="10px" />}
  </Center>
);

type PillProps = {
  checked: boolean;
  onClick: () => void;
  children: ReactNode;
};

const Pill = ({ checked, onClick, children }: PillProps) => (
  <Flex
    as="button"
    type="button"
    align="center"
    gap={3}
    p="14px 18px"
    minH="48px"
    border="2px solid"
    borderColor={checked ? 'oceanBoatBlue' : 'gray.200'}
    borderRadius="12px"
    bg={checked ? 'iceBlue' : 'white'}
    cursor="pointer"
    transition="all 0.15s ease"
    _hover={{ borderColor: 'oceanBoatBlue', bg: 'iceBlue' }}
    _active={{ transform: 'scale(0.98)' }}
    onClick={onClick}
    w="100%"
    textAlign="left"
  >
    <CheckCircle checked={checked} />
    {children}
  </Flex>
);

const PillSelect = ({ options, value, onChange, afterSelected }: PillSelectProps) => {
  const hasDescriptions = options.some((o) => o.description);
  return (
    <SimpleGrid columns={{ base: 1, md: hasDescriptions ? 1 : 2 }} spacing={3}>
      {options.map((option) => {
        const checked = value === option.value;
        return (
          <Fragment key={option.value}>
            <Pill checked={checked} onClick={() => onChange(option.value)}>
              <Box>
                <Text fontSize="md" fontWeight="600" color="grayDark">
                  {option.label}
                </Text>
                {option.description && (
                  <Text fontSize="sm" color="grayLight" mt={0.5}>
                    {option.description}
                  </Text>
                )}
              </Box>
            </Pill>
            {checked && afterSelected}
          </Fragment>
        );
      })}
    </SimpleGrid>
  );
};

export default PillSelect;
